import {
  AccountCircle,
  AccountTree,
  Article,
  ChevronLeft,
  ChevronRight,
  ContactSupport,
  DesignServices,
  ExitToApp,
  Groups,
  Home,
  HomeRepairService,
  Menu,
  Newspaper,
  Quiz,
  Reviews,
  RssFeed,
  Slideshow,
  Subscriptions,
  SupervisorAccount,
} from '@mui/icons-material'
import {
  AppBar,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material'
import { makeStyles, useTheme } from '@mui/styles'
import clsx from 'clsx'
import React, { useState } from 'react'
import { isMobile } from 'react-device-detect'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import translateUtils from '../../_helpers/translateUtils'
import { authService } from '../../_services'
import roleConstant from '../../constants/roles.constant'
// import Logo from '../../assets/images/logo.png'
import RoleBasedGuard from '../Guards/RoleBasedGuard'
import Routes from '../Routes/Routes'

const drawerWidth = 190
const useStyles = makeStyles((theme) => ({
  root: { display: 'flex', backgroundColor: '#18191b' },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    // backgroundColor: '#242527',
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  menuButton: { marginRight: 36, color: 'white' },
  hide: { display: 'none' },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  drawerOpen: {
    width: drawerWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    backgroundColor: '#242527',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  drawerClose: {
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: theme.spacing(7),
    [theme.breakpoints.up('sm')]: {
      width: theme.spacing(9),
    },
    backgroundColor: '#242527',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
    overflow: 'hidden',
    minHeight: '100vh',
  },
  '@global': {
    '*::-webkit-scrollbar': {
      width: '0.6em',
    },
    '*::-webkit-scrollbar-track': {
      '-webkit-box-shadow': 'inset 0 0 6px rgba(0,0,0,0.00)',
    },
    '*::-webkit-scrollbar-thumb': {
      backgroundColor: '#18191b',
      outline: '2px solid slategrey',
    },
  },
}))
const Layout = function () {
  const navigate = useNavigate()
  const location = useLocation()
  const classes = useStyles()
  const theme = useTheme()
  const [open, setOpen] = useState(!isMobile)
  const openDrawer = () => setOpen(true)
  const closeDrawer = () => setOpen(false)
  /**
   * Find icons here:
   * https://material-ui.com/components/material-icons/
   */
  const drawerItems = [
    {
      name: 'Blogs',
      route: '/blogs',
      icon: <RssFeed />,
      roles: Object.values(roleConstant),
    },
    {
      name: 'Design Works',
      route: '/design-works',
      icon: <DesignServices />,
      roles: Object.values(roleConstant),
    },
    {
      name: 'Fitout Projects',
      route: '/fitout-projects',
      icon: <AccountTree />,
      roles: Object.values(roleConstant),
    },
    {
      name: 'FAQs',
      route: '/faqs',
      icon: <Quiz />,
      roles: Object.values(roleConstant),
    },
    {
      name: 'News',
      route: '/news',
      icon: <Newspaper />,
      roles: Object.values(roleConstant),
    },
    {
      name: 'Pages',
      route: '/pages',
      icon: <Article />,
      roles: Object.values(roleConstant),
    },
    {
      // Dedicated entry — the homepage doc's structured fields (hero tagline,
      // About Us, intros, LocalBusiness) are only exposed on this route.
      name: 'Homepage',
      route: '/pages/homepage',
      icon: <Home />,
      roles: Object.values(roleConstant),
    },
    {
      name: 'Services',
      route: '/services',
      icon: <HomeRepairService />,
      roles: Object.values(roleConstant),
    },
    {
      name: 'Slides',
      route: '/slides',
      icon: <Slideshow />,
      roles: Object.values(roleConstant),
    },
    {
      name: 'Teams',
      route: '/teams',
      icon: <Groups />,
      roles: Object.values(roleConstant),
    },
    {
      name: 'Testimonials',
      route: '/testimonials',
      icon: <Reviews />,
      roles: Object.values(roleConstant),
    },
    {
      name: 'Videos',
      route: '/videos',
      icon: <Subscriptions />,
      roles: Object.values(roleConstant),
    },
    {
      name: 'Contacts',
      route: '/contacts',
      icon: <ContactSupport />,
      roles: Object.values(roleConstant),
    },
    {
      name: 'Admins',
      route: '/admins',
      icon: <SupervisorAccount />,
      roles: [roleConstant.ADMIN],
    },
  ].map((item, i) => (
    <RoleBasedGuard key={i} permitedRoles={item.roles}>
      <ListItemButton
        button
        key={item.name}
        className={
          // Exact match first so the two-segment /pages/homepage entry can
          // highlight (and /pages doesn't light up alongside it); every other
          // route keeps the original first-segment behavior.
          location.pathname.toLowerCase() === item.route.toLowerCase() ||
          (location.pathname.toLowerCase() !== '/pages/homepage' &&
            `/${location.pathname.split('/')[1].toLowerCase()}` ===
              item.route.toLowerCase())
            ? 'activeMenu'
            : ''
        }
        onClick={() => navigate(item.route)}
      >
        <Tooltip title={translateUtils.translate(item.name)} placement="right">
          <ListItemIcon style={{ color: 'white' }}>{item.icon}</ListItemIcon>
        </Tooltip>
        <ListItemText
          primary={translateUtils.translate(item.name)}
          style={{ color: 'white' }}
        />
      </ListItemButton>
      <Divider key={i} style={{ backgroundColor: 'rgba(255,255,255,0.12)' }} />
    </RoleBasedGuard>
  ))
  return (
    <div className={classes.root}>
      <CssBaseline />
      <AppBar
        position="fixed"
        className={clsx(classes.appBar, {
          [classes.appBarShift]: open,
        })}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            onClick={openDrawer}
            edge="start"
            className={clsx(classes.menuButton, {
              [classes.hide]: open,
            })}
          >
            <Menu />
          </IconButton>
          <div className="spread menu">
            {/* <img src={Logo} width="40px" alt="" /> */}
            <IconButton onClick={() => navigate(-1)} style={{ color: 'white' }}>
              <ChevronLeft />
            </IconButton>
            {!isMobile ? (
              <Typography variant="h6">
                {translateUtils.translate('Algedra Dashboard')}
              </Typography>
            ) : null}
            <div className="actions">
              <Link to="/profile">
                <AccountCircle />
              </Link>
              <Link onClick={() => authService.logout()} to="/">
                <ExitToApp />
              </Link>
            </div>
          </div>
        </Toolbar>
      </AppBar>
      <Drawer
        variant="permanent"
        className={clsx(classes.drawer, {
          [classes.drawerOpen]: open,
          [classes.drawerClose]: !open,
        })}
        classes={{
          paper: clsx({
            [classes.drawerOpen]: open,
            [classes.drawerClose]: !open,
          }),
        }}
      >
        <div className={classes.toolbar}>
          <IconButton onClick={closeDrawer} style={{ color: 'white' }}>
            {theme.direction === 'rtl' ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>
        </div>
        <Divider />
        <List>{drawerItems}</List>
      </Drawer>
      <main className={classes.content}>
        <div className={classes.toolbar} />
        <Routes />
      </main>
    </div>
  )
}
export default Layout
